import React, { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Container,
  Modal,
  Spinner,
} from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import {
  BsBoxArrowUpRight,
  BsEyeFill,
  BsFileEarmarkImage,
  BsFileEarmarkPdf,
  BsPaperclip,
} from 'react-icons/bs'
import { FaArrowLeft } from 'react-icons/fa'
import { LuGraduationCap } from 'react-icons/lu'
import toast from 'react-hot-toast'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import {
  fetchStudentHomework,
  getHomeworkErrorMessage,
  parseStudentHomeworkResponse,
} from './commonComponents/homeworkApi'
import { useInstitute } from './commonComponents/InstituteContext'

const formatDueDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const statusBadge = (s) => {
  const v = String(s || '').toLowerCase()
  if (v === 'published') return <Badge bg="success">Published</Badge>
  if (v === 'draft') return <Badge bg="secondary">Draft</Badge>
  if (v === 'archived') return <Badge bg="warning" text="dark">Archived</Badge>
  return <Badge bg="light" text="dark">{s || '—'}</Badge>
}

const getAttachmentKind = (url, label) => {
  const hay = `${url || ''} ${label || ''}`.toLowerCase()
  if (/\.pdf(\?|$)/i.test(hay)) return 'pdf'
  if (/\.(jpe?g|png|gif|webp)(\?|$)/i.test(hay)) return 'image'
  return 'file'
}

function HomeworkAttachmentCell({ row }) {
  if (!row?.attachment_url) return <span className="text-muted">—</span>
  const label = row.media_label && row.media_label !== '—' ? row.media_label : 'Attachment'
  const kind = getAttachmentKind(row.attachment_url, label)
  const Icon =
    kind === 'pdf' ? BsFileEarmarkPdf : kind === 'image' ? BsFileEarmarkImage : BsPaperclip

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span
        className="d-inline-flex align-items-center gap-2 rounded-2 px-2 py-1"
        style={{
          backgroundColor: '#F3F4F6',
          border: '1px solid #E5E7EB',
          fontSize: '12px',
          color: '#374151',
          maxWidth: '160px',
        }}
        title={label}
      >
        <Icon size={16} style={{ color: '#223843', flexShrink: 0 }} />
        <span className="text-truncate">{label}</span>
      </span>
      <Button
        type="button"
        variant="light"
        size="sm"
        className="d-inline-flex align-items-center gap-1 border"
        style={{ borderRadius: '8px', fontSize: '12px', fontWeight: 600, color: '#223843' }}
        onClick={() => window.open(row.attachment_url, '_blank', 'noopener,noreferrer')}
      >
        <BsBoxArrowUpRight size={14} />
        Open
      </Button>
    </div>
  )
}

export default function StudentHomework() {
  const { id: studentId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [viewTarget, setViewTarget] = useState(null)

  const loadHomework = useCallback(async () => {
    if (!studentId) return
    setLoading(true)
    try {
      const res = await fetchStudentHomework(studentId)
      setProfile(parseStudentHomeworkResponse(res))
    } catch (err) {
      toast.error(getHomeworkErrorMessage(err))
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }, [studentId])

  useEffect(() => {
    loadHomework()
  }, [loadHomework, location.key])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const rows = profile?.homework ?? []
  const studentName = profile?.student_name || 'Student'
  const fromParentProfile =
    location.state?.fromParentStudentProfile ||
    location.pathname.startsWith('/parent/student/profile/')
  const fromProfile = location.state?.fromStudentProfile

  const handleBack = () => {
    if (fromParentProfile && studentId) {
      navigate(`/parent/student/profile/${studentId}`)
      return
    }
    if (fromProfile && studentId) {
      navigate(`/student/profile/${studentId}`)
      return
    }
    navigate(-1)
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{ marginTop: isMobile ? '55px' : '-12px' }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#fff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold text-start"
                    style={{
                      color: '#223843',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {studentName}&apos;s Homework
                  </h3>
                  <p className="m-0 mt-0 text-muted small text-start">
                    {profile?.admission_number
                      ? `Admission: ${profile.admission_number}`
                      : 'Homework assignments'}
                    {profile?.class_name ? ` · Class: ${profile.class_name}` : ''}
                  </p>
                </div>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="mt-4 px-3 px-md-4"
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <Button
              variant="outline-secondary"
              onClick={handleBack}
              className="d-flex align-items-center gap-2"
            >
              <FaArrowLeft />
              {fromParentProfile ? 'Back to student profile' : 'Back to profile'}
            </Button>
            <Button
              variant="light"
              onClick={loadHomework}
              disabled={loading}
              className="d-flex align-items-center gap-2 border"
            >
              Refresh
            </Button>
          </div>

          <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px' }}>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted">Loading homework…</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-5 px-3">
                No homework found for this student.
              </div>
            ) : (
              <Table className="table mb-0">
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    {['Title', 'Subject', 'Due date', 'Attachment', 'Status', 'Action'].map(
                      (col) => (
                        <Th
                          key={col}
                          style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: '16px',
                            borderBottom: '1px solid #D1D3D4',
                            textAlign: col === 'Action' ? 'center' : 'left',
                          }}
                        >
                          {col}
                        </Th>
                      ),
                    )}
                  </Tr>
                </Thead>
                <Tbody className="text-start">
                  {rows.map((r, index) => (
                    <Tr
                      key={r.id ?? index}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? '#fff' : 'rgba(243, 244, 246, 0.4)',
                      }}
                    >
                      <Td style={{ padding: '16px', fontWeight: 600 }}>{r.title || '—'}</Td>
                      <Td style={{ padding: '16px' }}>{r.subject_name || '—'}</Td>
                      <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                        {formatDueDate(r.due_date)}
                      </Td>
                      <Td style={{ padding: '16px', minWidth: '200px' }}>
                        <HomeworkAttachmentCell row={r} />
                      </Td>
                      <Td style={{ padding: '16px' }}>{statusBadge(r.status)}</Td>
                      <Td className="text-center" style={{ padding: '16px' }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: '#223843',
                            color: '#fff',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                          }}
                          onClick={() => setViewTarget(r)}
                          title="View homework"
                        >
                          <BsEyeFill size={14} />
                        </button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Card>
        </Container>
      </div>

      <Modal show={Boolean(viewTarget)} onHide={() => setViewTarget(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{viewTarget?.title || 'Homework'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-start">
          <p>
            <strong>Subject:</strong> {viewTarget?.subject_name || '—'}
          </p>
          <p>
            <strong>Class:</strong>{' '}
            {viewTarget?.class_name_only || viewTarget?.class_name || profile?.class_name || '—'}
          </p>
          <p>
            <strong>Status:</strong> {viewTarget?.status || '—'}
          </p>
          <p>
            <strong>Due date:</strong> {formatDueDate(viewTarget?.due_date)}
          </p>
          <p>
            <strong>Description:</strong>
            <br />
            {viewTarget?.description || '—'}
          </p>
          <div className="mb-2">
            <strong className="d-block mb-2">Attachment</strong>
            {viewTarget?.attachment_url ? (
              <HomeworkAttachmentCell row={viewTarget} />
            ) : (
              '—'
            )}
          </div>
          <p className="text-muted small mb-0">
            Created: {formatDateTime(viewTarget?.created_at)}
          </p>
        </Modal.Body>
      </Modal>
    </>
  )
}
